// Zustand store pentru autentificare cu Supabase
// OWNER: echipa API & FE
// Orice modificare trebuie documentată în DEV_LOG.md
import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { supabaseAuthService, AuthErrorType } from "../services/supabaseAuthService";
import type { AuthUser } from "../services/supabaseAuthService";
import { queryClient } from "../queryClient";

// 🔄 React 19 use() hook patterns - using standard async patterns for store
// import { useConditionalPromise, preloadPromise } from "../hooks/usePromise";
import {
  BaseStoreState,
  BaseStoreActions,
  storeLogger,
  createDevtoolsOptions,
  createAsyncAction,
  createPersistConfig,
} from "./storeUtils";

/**
 * Interface pentru Auth Store cu pattern-uri standardizate
 */
export interface AuthState extends BaseStoreState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  errorType?: AuthErrorType;

  // Acțiuni async standardizate
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkUser: () => Promise<void>;

  // Acțiuni sync
  clearAuthError: () => void;
  setUser: (user: AuthUser | null) => void;
}

const STORE_NAME = "AuthStore";

/**
 * Store pentru autentificare cu pattern-uri moderne Zustand
 * Integrare cu Supabase Auth și persistență optimizată
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => {
        // Helper pentru setări standardizate
        const setLoading = (loading: boolean) => {
          set({ loading, lastUpdated: new Date() }, false, "setLoading");
        };

        const setError = (error: string | null, errorType?: AuthErrorType) => {
          set({ error, errorType, lastUpdated: new Date() }, false, "setError");
          if (error) {
            storeLogger.error(STORE_NAME, "Auth Error", { error, errorType });
          }
        };

        // Creăm acțiuni async standardizate
        const createAuthAction = <T extends readonly unknown[]>(
          actionName: string,
          action: (...args: T) => Promise<unknown>,
        ) =>
          createAsyncAction(
            STORE_NAME,
            actionName,
            action,
            setLoading,
            setError,
          );

        return {
          // State inițial
          user: null,
          isAuthenticated: false,
          loading: false,
          error: null,
          errorType: undefined,
          lastUpdated: new Date(),

          // Acțiuni async cu error handling standardizat
          login: createAuthAction(
            "login",
            async (email: string, password: string) => {
              const result = await supabaseAuthService.login(email, password);

              if (result.error) {
                setError(result.error, result.errorType);
                throw new Error(result.error);
              }

              set(
                {
                  user: result.user,
                  isAuthenticated: !!result.user,
                  error: null,
                  errorType: undefined,
                  lastUpdated: new Date(),
                },
                false,
                "login_success",
              );

              storeLogger.info(STORE_NAME, "Login successful", {
                userId: result.user?.id,
              });
            },
          ),

          loginWithGoogle: createAuthAction(
            "loginWithGoogle",
            async () => {
              const result = await supabaseAuthService.loginWithGoogle();

              if (result.error) {
                setError(result.error, AuthErrorType.NETWORK);
                throw new Error(result.error);
              }

              // Pentru Google OAuth, user-ul va fi setat prin auth state change listener
              // Nu setăm manual user-ul aici pentru că redirectul se va întâmpla
              storeLogger.info(STORE_NAME, "Google login initiated");
            },
          ),

          register: createAuthAction(
            "register",
            async (email: string, password: string) => {
              const result = await supabaseAuthService.register(
                email,
                password,
              );

              if (result.error) {
                setError(result.error, result.errorType);
                throw new Error(result.error);
              }

              set(
                {
                  user: result.user,
                  isAuthenticated: !!result.user,
                  error: null,
                  errorType: undefined,
                  lastUpdated: new Date(),
                },
                false,
                "register_success",
              );

              storeLogger.info(STORE_NAME, "Registration successful", {
                userId: result.user?.id,
              });
            },
          ),

          logout: createAuthAction("logout", async () => {
            await supabaseAuthService.logout();

            set(
              {
                user: null,
                isAuthenticated: false,
                error: null,
                errorType: undefined,
                lastUpdated: new Date(),
              },
              false,
              "logout_success",
            );

            storeLogger.info(STORE_NAME, "Logout successful");
          }),

          checkUser: createAuthAction("checkUser", async () => {
            const user = await supabaseAuthService.getCurrentUser();

            set(
              {
                user,
                isAuthenticated: !!user,
                error: null,
                lastUpdated: new Date(),
              },
              false,
              "checkUser_success",
            );

            storeLogger.info(STORE_NAME, "User check completed", {
              isAuthenticated: !!user,
              userId: user?.id,
            });
          }),

          // Acțiuni sync standardizate
          clearAuthError: () => {
            set({ error: null, errorType: undefined }, false, "clearAuthError");
            storeLogger.info(STORE_NAME, "Auth error cleared");
          },

          setUser: (user: AuthUser | null) => {
            set(
              {
                user,
                isAuthenticated: !!user,
                lastUpdated: new Date(),
              },
              false,
              "setUser",
            );
            storeLogger.info(STORE_NAME, "User set manually", {
              userId: user?.id,
            });
          },

          // Implementăm base actions
          setLoading,
          setError: (error: string | null) => setError(error),
          clearError: () => setError(null),
          reset: () => {
            set(
              {
                user: null,
                isAuthenticated: false,
                loading: false,
                error: null,
                errorType: undefined,
                lastUpdated: new Date(),
              },
              false,
              "reset",
            );
            storeLogger.info(STORE_NAME, "Store reset");
          },
        };
      },
      createPersistConfig("auth", (state: AuthState) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      })),
    ),
    createDevtoolsOptions(STORE_NAME),
  ),
);

// Configurare listener pentru schimbări de autentificare
supabaseAuthService.onAuthStateChange((event, session) => {
  const currentState = useAuthStore.getState();

  storeLogger.info(STORE_NAME, "Auth state change", {
    event,
    sessionExists: !!session,
  });

  if (event === "SIGNED_IN" && session?.user) {
    useAuthStore.setState({
      user: {
        id: session.user.id,
        email: session.user.email || "",
      },
      isAuthenticated: true,
      loading: false,
      error: null,
      errorType: undefined,
      lastUpdated: new Date(),
    });
  } else if (event === "SIGNED_OUT") {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      errorType: undefined,
      lastUpdated: new Date(),
    });
  }
});

// 🎯 FIX TASK 1.1: Cache invalidation subscriber pentru userId changes
let previousUserId: string | null = null;

useAuthStore.subscribe((state) => {
  const currentUserId = state.user?.id || null;
  
  // Dacă userId s-a schimbat, invalidează cache-ul pentru user-ul anterior și curent
  if (previousUserId !== currentUserId) {
    storeLogger.info(STORE_NAME, "UserId changed - invalidating cache", {
      previousUserId,
      currentUserId,
    });

    // Șterge cache-ul pentru user-ul anterior (dacă exista) pentru securitate
    if (previousUserId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 (queryKey.includes('transactions') || 
                  queryKey.includes('recurring') ||
                  queryKey.includes('accounts')) &&
                 queryKey.includes(previousUserId);
        }
      });
      storeLogger.info(STORE_NAME, `Removed cache for previous user: ${previousUserId}`);
    }

    // Invalidează cache-ul pentru user-ul curent (dacă există)
    if (currentUserId) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && 
                 (queryKey.includes('transactions') || 
                  queryKey.includes('recurring') ||
                  queryKey.includes('accounts')) &&
                 queryKey.includes(currentUserId);
        }
      });
    }

    // La logout, invalidează tot cache-ul pentru siguranță
    if (!currentUserId && previousUserId) {
      queryClient.clear();
      storeLogger.info(STORE_NAME, "User logged out - cleared entire cache");
    }

    previousUserId = currentUserId;
  }
});

// Global window extension for E2E testing
declare global {
  interface Window {
    __playwright?: unknown;
    useAuthStore?: typeof useAuthStore;
  }
}

// 🧪 E2E Testing Support: Expose store globally for testing
if (typeof window !== 'undefined') {
  // Check if we're in a testing environment
  const isE2E = window.location.hostname === 'localhost' && 
               (window.navigator.userAgent.includes('HeadlessChrome') || 
                window.navigator.webdriver === true ||
                window.__playwright !== undefined);
  
  if (isE2E || process.env.NODE_ENV === 'development') {
    window.useAuthStore = useAuthStore;
    console.log('🧪 AuthStore exposed globally for E2E testing');
  }
}

// Selectori optimizați pentru performance
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useAuthError = () =>
  useAuthStore((state) => ({
    error: state.error,
    errorType: state.errorType,
  }));

// =============================================================================
// 🚀 REACT 19 USE() HOOK ENHANCED AUTH PATTERNS
// =============================================================================

export interface AuthV2Options {
  enablePreloading?: boolean;
  useOptimisticAuth?: boolean;
  enableAsyncValidation?: boolean;
}

/**
 * React 19 enhanced auth hooks with use() hook optimizations
 * 
 * Benefits:
 * - Preloaded auth service for faster operations
 * - Promise-based auth operations with use() hook integration
 * - Better Suspense integration for auth flows
 * - Enhanced async validation patterns
 */

// Enhanced auth user hook with preloading
export function useAuthUserV2(options: AuthV2Options = {}) {
  const { enablePreloading = true } = options;
  
  const user = useAuthUser();
  
  // 🔄 React 19: Preload auth service for faster operations
  React.useEffect(() => {
    if (enablePreloading) {
      // preloadPromise(
      //   import("../services/supabaseAuthService"),
      //   'auth-service',
      //   'AuthServicePreload'
      // );
    }
  }, [enablePreloading]);
  
  return user;
}

// Enhanced authentication checker with use() hook
export function useAuthValidationV2(options: AuthV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  
  // 🔄 React 19: Conditional async validation with use() hook
  // React Compiler optimizes - no useMemo needed
  const validationPromise = (() => {
    if (!enableAsyncValidation || !user) return null;
    
    return supabaseAuthService.getCurrentUser().then(validatedUser => ({
      isValid: !!validatedUser && validatedUser.id === user.id,
      user: validatedUser,
      timestamp: Date.now()
    }));
  })();
  
  // const validation = useConditionalPromise(
  //   enableAsyncValidation && !!user && !!validationPromise,
  //   validationPromise!,
  //   { isValid: isAuthenticated, user, timestamp: Date.now() },
  //   {
  //     cacheKey: `auth-validation-${user?.id}`,
  //     retry: { attempts: 1, delay: 500 },
  //     name: 'AuthValidation'
  //   }
  // );
  const validation = { isValid: isAuthenticated, user, timestamp: Date.now() };
  
  return {
    user,
    isAuthenticated,
    validation: validation || { isValid: isAuthenticated, user, timestamp: Date.now() }
  };
}

// Enhanced login hook with React 19 patterns
export function useLoginV2(options: AuthV2Options = {}) {
  const { enablePreloading = true, useOptimisticAuth = false } = options;
  const { login, loading, error } = useAuthStore();
  
  // 🔄 React 19: Enhanced login with preloaded service
  const enhancedLogin = async (email: string, password: string) => {
    // React Compiler optimizes - no useCallback needed
    console.log('🚀 [React 19 Auth] Starting enhanced login');
    
    try {
      // Preload auth service if needed
      if (enablePreloading) {
        await import("../services/supabaseAuthService");
      }
      
      // Optimistic update if enabled
      if (useOptimisticAuth) {
        useAuthStore.setState({ loading: true, error: null });
      }
      
      await login(email, password);
      
      console.log('✅ [React 19 Auth] Enhanced login successful');
      
    } catch (error) {
      console.error('🚨 [React 19 Auth] Enhanced login failed:', error);
      throw error;
    }
  };
  
  return {
    login: enhancedLogin,
    loading,
    error
  };
}

// Enhanced logout hook with React 19 patterns
export function useLogoutV2(options: AuthV2Options = {}) {
  const { useOptimisticAuth = false } = options;
  const { logout, loading } = useAuthStore();
  
  const enhancedLogout = async () => {
    // React Compiler optimizes - no useCallback needed
    console.log('🚀 [React 19 Auth] Starting enhanced logout');
    
    try {
      // Optimistic update if enabled
      if (useOptimisticAuth) {
        useAuthStore.setState({ loading: true });
      }
      
      await logout();
      
      console.log('✅ [React 19 Auth] Enhanced logout successful');
      
    } catch (error) {
      console.error('🚨 [React 19 Auth] Enhanced logout failed:', error);
      throw error;
    }
  };
  
  return {
    logout: enhancedLogout,
    loading
  };
}

// Enhanced auth status hook with better reactivity
export function useAuthStatusV2(options: AuthV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const loading = useAuthLoading();
  const error = useAuthError();
  
  // Enhanced status with async validation
  const validation = useAuthValidationV2({ enableAsyncValidation });
  
  return {
    user,
    isAuthenticated: validation.validation.isValid,
    loading,
    error,
    lastValidated: validation.validation.timestamp,
    isValidationEnabled: enableAsyncValidation
  };
}

// Preload auth dependencies for better performance
export function useAuthPreloadV2() {
  React.useEffect(() => {
    // Preload auth service
    // preloadPromise(
    //   import("../services/supabaseAuthService"),
    //   'auth-service-global',
    //   'GlobalAuthServicePreload'
    // );
    
    // Preload query client utilities
    // preloadPromise(
    //   import("../queryClient"),
    //   'query-client',
    //   'QueryClientPreload'
    // );
    
    console.log('🚀 [React 19 Auth] Auth dependencies preloaded');
  }, []);
}

// Enhanced auth guard hook with React 19 patterns
export function useAuthGuardV2(options: AuthV2Options = {}) {
  const { enableAsyncValidation = true } = options;
  const authStatus = useAuthStatusV2({ enableAsyncValidation });
  
  // 🔄 React 19: Promise-based guard that can work with Suspense
  // React Compiler optimizes - no useMemo needed
  const guardPromise = (() => {
    if (authStatus.isAuthenticated) {
      return Promise.resolve(true);
    } else if (authStatus.loading) {
      return new Promise(resolve => {
        const checkAuth = () => {
          const currentStatus = useAuthStore.getState();
          if (!currentStatus.loading) {
            resolve(currentStatus.isAuthenticated);
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      });
    } else {
      return Promise.resolve(false);
    }
  })();
  
  return {
    isAuthenticated: authStatus.isAuthenticated,
    loading: authStatus.loading,
    guardPromise
  };
}
